import { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function QuestionCard({ question, value, onChange, showFeedback = false, disabled = false }) {
  const [localValue, setLocalValue] = useState(value);
  const [isCorrect, setIsCorrect] = useState(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue) => {
    if (disabled) return;

    setLocalValue(newValue);
    onChange(question.id, newValue);
    
    // For immediate feedback
    if (showFeedback) {
      checkAnswer(newValue);
    }
  };

  // When the user presses "Check Answers" (showFeedback becomes true),
  // evaluate the current answer so feedback shows even if the input isn't changed.
  useEffect(() => {
    if (showFeedback) {
      checkAnswer(localValue);
    } else {
      setIsCorrect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedback]);

  const checkAnswer = (answer) => {
    if (answer === undefined || answer === null || answer === '') {
      setIsCorrect(null);
      return;
    }
    
    const correct = String(answer).toLowerCase() === String(question.answer).toLowerCase();
    setIsCorrect(correct);
    return correct;
  };

  const renderInput = () => {
    const inputClass = `mt-2 block w-full max-w-2xl rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 py-2 ${
      showFeedback && isCorrect !== null
        ? isCorrect
          ? 'border-green-500 bg-green-50'
          : 'border-red-500 bg-red-50'
        : ''
    } ${disabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`;

    switch (question.type) {
      case 'boolean':
        return (
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0 mt-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="h-4 w-4 text-blue-600"
                checked={localValue === true}
                onChange={() => handleChange(true)}
                disabled={disabled}
              />
              <span className="ml-2 text-gray-700">True</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                className="h-4 w-4 text-blue-600"
                checked={localValue === false}
                onChange={() => handleChange(false)}
                disabled={disabled}
              />
              <span className="ml-2 text-gray-700">False</span>
            </label>
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            className={inputClass}
            value={localValue || ''}
            onChange={(e) => handleChange(Number(e.target.value))}
            min={0}
            step={1}
            disabled={disabled}
          />
        );
      
      default: // text
        return (
          <textarea
            className={`${inputClass} resize-y min-h-[3.5rem]`}
            rows={3}
            value={localValue || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className={`rounded-lg bg-white p-4 sm:p-5 shadow-sm sm:shadow ${showFeedback && isCorrect !== null ? (isCorrect ? 'ring-2 ring-green-500' : 'ring-2 ring-red-500') : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-base sm:text-sm font-medium text-gray-900 leading-snug">
            {question.question}
          </h3>
          <div className="mt-2">
            {renderInput()}
          </div>
        </div>
        
        {showFeedback && isCorrect !== null && (
          <div className="ml-4 flex-shrink-0">
            {isCorrect ? (
              <CheckCircleIcon className="h-6 w-6 text-green-500" />
            ) : (
              <XCircleIcon className="h-6 w-6 text-red-500" />
            )}
          </div>
        )}
      </div>
      
      {showFeedback && isCorrect === false && (
        <div className="mt-2 text-sm text-red-600">
          <p>Correct answer: <span className="font-medium">{question.answer}</span></p>
        </div>
      )}
    </div>
  );
}
